import Dexie, { type EntityTable } from "dexie";
import { BasketData, calculateBasketTotal } from "../utils/basket_helpers";

export type BundleRequirement = {
  count: number;
  requiredProductId: number;
}[];

interface Product {
  id: number;
  name: string;
  sku: string;
  price: number;
  description: string;
  originalStockQuantity: number;
  imageBlob?: Blob;
  imageMimeType?: string;
}
interface Sale {
  id: number;
  timestamp: Date;
  paymentMethod?: string;
  notes?: string;
  salePrice?: number;
  products: string; // Stored as JSON string
  bundles: string; // Stored as JSON string
}

interface SaleParsed {
  id: number;
  timestamp: Date;
  paymentMethod?: string;
  notes?: string;
  salePrice?: number;
  products: Array<{
    product: Product;
    quantity: number;
    priceAtTime: number;
  }>;
  bundles: Bundle[];
}

interface Bundle {
  id: number;
  name: string;
  requirements: string; // Stored as JSON string
  discountAmount: number;
}

interface BundleParsed {
  id: number;
  name: string;
  requirements: BundleRequirement;
  discountAmount: number;
}

// Helper functions for bundle serialization
export const serialiseBundle = (bundle: BundleParsed): Bundle => {
  return {
    ...bundle,
    requirements: JSON.stringify(bundle.requirements),
  };
};

export const parseBundle = (bundle: Bundle): BundleParsed => {
  return {
    ...bundle,
    requirements: JSON.parse(bundle.requirements),
  };
};

// Helper functions for sale serialization
export const serialiseSale = (sale: SaleParsed): Sale => {
  return {
    id: sale.id,
    timestamp: sale.timestamp,
    paymentMethod: sale.paymentMethod,
    notes: sale.notes,
    salePrice: sale.salePrice,
    products: JSON.stringify(sale.products),
    bundles: JSON.stringify(sale.bundles),
  };
};

export const parseSale = (sale: Sale): SaleParsed => {
  return {
    id: sale.id,
    timestamp: sale.timestamp,
    paymentMethod: sale.paymentMethod,
    notes: sale.notes,
    salePrice: sale.salePrice,
    products: JSON.parse(sale.products),
    bundles: JSON.parse(sale.bundles),
  };
};

// Helper function to create a new sale
export const createNewSale = (
  basketData: BasketData,
  paymentMethod?: string
): Omit<SaleParsed, "id"> => {
  // Calculate the sale price
  const calculatedTotal = calculateBasketTotal(basketData);
  return {
    timestamp: new Date(),
    paymentMethod,
    notes: basketData.notes || undefined,
    salePrice: basketData.adjustedTotal || calculatedTotal,
    products: basketData.items.map((item) => ({
      product: {
        id: item.id,
        name: item.name,
        sku: item.sku,
        price: item.price,
        description: item.description,
        originalStockQuantity: item.originalStockQuantity,
      },
      quantity: item.quantity,
      priceAtTime: item.price,
    })),
    bundles: basketData.bundles,
  };
};

// Helper function to save a sale to the database
export const saveSale = async (
  sale: Omit<SaleParsed, "id">
): Promise<number> => {
  const serializedSale: Omit<Sale, "id"> = {
    timestamp: sale.timestamp,
    paymentMethod: sale.paymentMethod,
    notes: sale.notes,
    salePrice: sale.salePrice,
    products: JSON.stringify(sale.products),
    bundles: JSON.stringify(sale.bundles),
  };

  return await db.sales.add(serializedSale);
};

const db = new Dexie("ConventionPOS") as Dexie & {
  products: EntityTable<Product, "id">;
  sales: EntityTable<Sale, "id">;
  bundles: EntityTable<Bundle, "id">;
};

// Schema declaration
db.version(3).stores({
  products: "++id, name, price, sku, description, originalStockQuantity",
  sales: "++id, timestamp, total, paymentMethod",
  bundles: "++id, name, bundleDescription, discountAmount, discountType",
});

export type { Product, Sale, Bundle, SaleParsed, BundleParsed };
export { db };
